/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable no-nested-ternary */
import { CellContext } from '@tanstack/react-table';
import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { DisplayOccasionWithIcon } from '@/components/DisplayOccasionWithIcon/DisplayOccasionWithIcon';
import StarTooltip from '@/components/Icons/CustomIcons/StarTooltip';
import Link from '@/components/Link/Link';
import Table from '@/components/Tables/Table/Table';
import { useBundlesForGroup } from '@/services/bundles/bundles.service';
import { BundleDetails } from '@/types/bundles.types';
import genericErrorShowToastOptions from '@/utils/genericErrorMessage/genericErrorMessage';
import useNavigateWithToast from '@/utils/useNavigateWithToast/useNavigateWithToast';
import styles from './ActiveBundlesTable.module.css';
import { Trans, useTranslation } from 'react-i18next';
import LinkIcon from '@mui/icons-material/Link';

const DisplayStarTooltip = ({ id, bundleCode }: { id: string; bundleCode: string }) => {
  const { t } = useTranslation();
  return (
    <div>
      <StarTooltip content={t('activeBundles.nationalProgram')} className={styles.starSvg} position="top" />
      <Link to={`/bundles/${id}`}>{bundleCode}</Link>
    </div>
  );
};

const DisplayLink = ({ id, bundleCode }: { id: string; bundleCode: string }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <Link to={`/bundles/${id}`}>{bundleCode}</Link>
    <LinkIcon style={{ fontSize: '20px', color: '#266DCC' }} />
  </div>
);

const StarTooltipCell = ({ row }: CellContext<BundleDetails, unknown>) => {
  const { id, code, linkedBundleName, isNationalProgramLiveOrOptIn } = row.original;
  return (
    <>
      {isNationalProgramLiveOrOptIn ? (
        <DisplayStarTooltip id={id} bundleCode={code} />
      ) : linkedBundleName ? (
        <DisplayLink id={id} bundleCode={code} />
      ) : (
        <Link to={`/bundles/${id}`}>{code}</Link>
      )}
    </>
  );
};

const ActiveBundlesTable: FC = () => {
  const { groupId } = useParams();
  const { t } = useTranslation();
  const navigateWithToast = useNavigateWithToast();
  const { data, isLoading, isError } = useBundlesForGroup(groupId !== undefined, groupId!);

  const yesNoTranslation = (value = false) => (value ? t('common.yes') : t('common.no'));

  if (isError) {
    navigateWithToast('/', {
      ...genericErrorShowToastOptions,
      id: 'errorFetchingBundlesForGroup',
      label: t('error.fetchingBundles'),
    });
  }

  return (
    <div className={styles.tablePage}>
      {/* out of scope for this story */}
      {/* <Button onClick={() => navigate('/bundles')}>Manage Bundles</Button> */}
      <div className={styles.tableContainer}>
        <Table
          isLoading={isLoading}
          includeMinWidth={false}
          width="100%"
          height="calc(100vh - 460px)"
          data={data?.data || []}
          headersToWrap={['visibleOnline', 'redeemOnline']}
          columns={[
            {
              id: 'bundleCode',
              enableSorting: true,
              header: t('activeBundles.table.bundleCode'),
              cell: StarTooltipCell,
            },
            {
              id: 'displayName',
              enableSorting: true,
              header: t('activeBundles.table.displayName'),
              accessorFn: (originalRow: BundleDetails) => originalRow.name || '-',
            },
            {
              id: 'bundleDesc',
              accessorKey: 'internalName',
              enableSorting: true,
              header: t('activeBundles.table.bundleDesc'),
              accessorFn: (originalRow: BundleDetails) => originalRow.internalName || '-',
            },
            {
              id: 'price',
              enableSorting: true,
              header: t('activeBundles.table.bundleDesc'),
            },
            {
              id: 'occasions',
              accessorKey: 'occasions',
              enableSorting: true,
              header: t('activeBundles.table.occasion'),
              cell: DisplayOccasionWithIcon,
            },
            {
              id: 'redeemOnline',
              accessorKey: 'isRedeemableOnline',
              enableSorting: true,
              header: t('activeBundles.table.redeemOnline'),
              accessorFn: (originalRow) =>
                yesNoTranslation(
                  originalRow.isNationalProgramLiveOrOptIn
                    ? originalRow.nationalProgramRedeemableOnline
                    : originalRow.isRedeemableOnline,
                ),
            },
            {
              id: 'visibleOnline',
              accessorKey: 'isVisibleOnline',
              enableSorting: true,
              header: t('activeBundles.table.visibleOnline'),
              accessorFn: (originalRow) =>
                yesNoTranslation(
                  originalRow.isNationalProgramLiveOrOptIn
                    ? originalRow.nationalProgramVisibleOnline
                    : originalRow.isVisibleOnline,
                ),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default ActiveBundlesTable;
